import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { Navigation } from '../../components/Navigation';
import { Chat } from '../../components/Chat';
import { MessageSquare, Trash2, Briefcase } from 'lucide-react';

interface Conversation {
  application_id: string;
  job_title: string;
  company_name: string;
  last_message_at: string;
  unread_count: number;
}

interface ApplicationWithJob {
  id: string;
  job: {
    title: string;
    company: string;
  };
}

export function JobSeekerMessages() {
  const { profile } = useAuthStore();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [selectedJobInfo, setSelectedJobInfo] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      loadConversations();
    }
  }, [profile]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const { data: applications } = await supabase
        .from('applications')
        .select(`
          id,
          job:job_id (
            title,
            company
          )
        `)
        .eq('applicant_id', profile!.id);

      if (!applications) {
        setConversations([]);
        setLoading(false);
        return;
      }

      const conversationsData: Conversation[] = [];
      const typedApplications = applications as unknown as ApplicationWithJob[];

      for (const app of typedApplications) {
        const { data: messages } = await supabase
          .from('messages')
          .select('created_at, sender_id, read')
          .eq('application_id', app.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (messages && messages.length > 0) {
          const unreadMessages = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('application_id', app.id)
            .eq('read', false)
            .neq('sender_id', profile!.id);

          conversationsData.push({
            application_id: app.id,
            job_title: app.job.title,
            company_name: app.job.company,
            last_message_at: messages[0].created_at,
            unread_count: unreadMessages.count || 0,
          });
        }
      }

      conversationsData.sort((a, b) =>
        new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      );

      setConversations(conversationsData);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConversation = async (applicationId: string) => {
    if (deletingConversationId === applicationId) {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('application_id', applicationId);

      if (!error) {
        setDeletingConversationId(null);
        if (selectedApplicationId === applicationId) {
          setSelectedApplicationId(null);
        }
        loadConversations();
      }
    } else {
      setDeletingConversationId(applicationId);
      setTimeout(() => setDeletingConversationId(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Messages</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 bg-white rounded-xl shadow-lg p-4 border-2 border-gray-200 h-fit max-h-[calc(100vh-12rem)] overflow-y-auto">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-orange-500" />
                Conversations
              </h2>

              {conversations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No conversations yet</p>
                  <p className="text-gray-400 text-xs mt-2">Messages with employers will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {conversations.map((conv) => {
                    const isDeleting = deletingConversationId === conv.application_id;
                    const isSelected = selectedApplicationId === conv.application_id;

                    return (
                      <div
                        key={conv.application_id}
                        className={`p-3 rounded-lg border-2 transition cursor-pointer ${
                          isSelected
                            ? 'bg-orange-50 border-orange-500'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div
                            onClick={() => {
                              setSelectedApplicationId(conv.application_id);
                              setSelectedJobInfo(`${conv.company_name} - ${conv.job_title}`);
                            }}
                            className="flex-1"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                                <Briefcase className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 text-sm">{conv.company_name}</p>
                                <p className="text-xs text-gray-600 truncate">{conv.job_title}</p>
                              </div>
                              {conv.unread_count > 0 && (
                                <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {new Date(conv.last_message_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConversation(conv.application_id);
                            }}
                            className={`ml-2 p-1 rounded transition ${
                              isDeleting ? 'bg-red-100' : 'hover:bg-gray-200'
                            }`}
                            title={isDeleting ? 'Click again to confirm' : 'Delete conversation'}
                          >
                            <Trash2 className={`w-4 h-4 ${isDeleting ? 'text-red-600' : 'text-gray-600'}`} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              {selectedApplicationId ? (
                <Chat
                  applicationId={selectedApplicationId}
                  recipientName={selectedJobInfo}
                />
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center border-2 border-gray-200">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Select a conversation</h2>
                  <p className="text-gray-600">Choose a conversation from the list to start messaging</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
